import Swal from "sweetalert2";
import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import InactivityModal from "@/components/InactivityTracker/InactivityTracker.vue";
import { get_token, get_user_details } from "@/router";

import {
  campaign_state_methods,
  campaign_view_small_methods,
} from "../DonationCampaigns/donation_campaigns_helper";
import {
  get_api_error_message,
  matches_campaign_filter,
  matches_campaign_query,
  update_campaign_in_lists,
} from "../DonationCampaigns/donation_campaigns_form_helper";
import {
  campaign_delete_methods,
} from "../DonationCampaigns/donation_campaigns_delete_helper";
import {
  get_campaign_cover_image,
  get_campaign_supporting_files,
  get_campaign_summary,
} from "../DonationCampaigns/donation_campaigns_methods_helper";
import { my_donation_campaigns_methods } from "./my_donation_campaigns_methods_helper";
import { build_campaign_preview, my_donation_campaigns_flow_methods } from "./my_donation_campaigns_flow_helper";

export default {
  name: "MyDonationCampaignsView",
  components: {
    HomeNavBar,
    InactivityModal,
  },
  data() {
    return ({
      token_exists: false,
      user_details: {
        user_role: "",
        first_name: "",
        last_name: "",
        user_id: "",
      },
      active_tab: "manage",
      manage_filter: "all",
      search_query: "",
      public_campaigns: [],
      my_campaigns_data: [],
      categories: [],
      selected_campaign_id: null,
      manage_campaign_id: null,
      campaign_form: {},
      editing_campaign_id: null,
      campaign_document_files: [],
      is_loading_my_campaigns: false,
      is_loading_categories: false,
      is_saving_campaign: false,
      is_uploading_campaign_image: false,
      is_uploading_supporting_files: false,
      is_toggling_campaign_id: null,
      is_deleting_campaign_id: null,
      preview_file: null,
      errors: {},
    });
  },
  computed: {
    page_loading() {
      return this.is_loading_my_campaigns || this.is_loading_categories;
    },
    editing_campaign() {
      return this.my_campaigns_data.find((campaign) => Number(campaign.id) === Number(this.editing_campaign_id)) || null;
    },
    preview_campaign() {
      return build_campaign_preview(this.campaign_form, this.get_category_name.bind(this), this.calculate_days_left.bind(this));
    },
    is_editing_campaign() {
      return !!this.editing_campaign_id;
    },
    editing_campaign_supporting_files() {
      return get_campaign_supporting_files(this.editing_campaign || {});
    },
    combined_campaign_supporting_files() {
      const existing_files = this.editing_campaign_supporting_files.map((file) => ({
        id: file?.id,
        source: "existing",
        file,
      }));

      const pending_files = this.campaign_document_files.map((file, index) => ({
        id: `pending-${file?.name || "file"}-${index}`,
        source: "pending",
        file,
      }));

      return [...existing_files, ...pending_files];
    },
    my_campaigns() {
      const query = String(this.search_query || "").trim().toLowerCase();
      return this.my_campaigns_data.filter((campaign) => (
        matches_campaign_filter(campaign, this.manage_filter) &&
        matches_campaign_query(campaign, query)
      ));
    },
  },
  mounted() {
    this.token_exists = !!get_token();
    this.user_details = get_user_details() || this.user_details;
    this.reset_form();
    this.fetch_campaign_categories_api();
    this.fetch_my_campaigns_api();
  },
  methods: {
    getToken() { return get_token(); },
    get_campaign_summary,
    toggleSidebar() { return null; },
    get_request_headers() { return { Authorization: `Token ${this.getToken()}` }; },
    get_api_error_message,
    handle_campaign_save_success(saved_campaign, title) {
      this.my_campaigns_data = [saved_campaign, ...this.my_campaigns_data];
      this.selected_campaign_id = saved_campaign.id;
      this.active_tab = "manage";
      this.reset_form();
      Swal.fire({
        icon: "success",
        title,
        showConfirmButton: false,
        timer: 2200,
        position: "bottom-end",
        toast: true
      });
    },
    create_campaign() {
      if (!this.validate_campaign_form()) {
        return;
      }

      this.is_saving_campaign = true;
      this.clear_form_errors();

      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/create_campaign/`,
        method: "POST",
        headers: this.get_request_headers(),
        data: this.build_campaign_form_data(),
        processData: false,
        contentType: false,
        success: (response_data) => {
          this.handle_campaign_save_success(response_data?.data || {}, "Campaign draft created.");
        },
        error: (error) => {
          this.handle_form_request_error(error, "Unable to save campaign.");
        },
        complete: () => {
          this.is_saving_campaign = false;
        },
      });
    },
    submit_campaign() {
      return this.is_editing_campaign ? this.update_campaign() : this.create_campaign();
    },
    handle_campaign_update_success(updated_campaign) {
      const updated_lists = update_campaign_in_lists({
        updated_campaign,
        my_campaigns_data: this.my_campaigns_data,
        public_campaigns: this.public_campaigns,
      });

      this.my_campaigns_data = updated_lists.my_campaigns_data;
      this.selected_campaign_id = updated_lists.selected_campaign_id;
      this.active_tab = "manage";
      this.reset_form();
      Swal.fire({
        icon: "success",
        title: "Campaign updated.",
        showConfirmButton: false,
        timer: 2200,
        position: "bottom-end",
        toast: true
      });
    },
    update_campaign() {
      if (!this.validate_campaign_form() || !this.editing_campaign_id) {
        return;
      }

      const form_data = this.build_campaign_form_data();
      form_data.append("campaign_id", this.editing_campaign_id);

      this.is_saving_campaign = true;
      this.clear_form_errors();

      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/update_campaign/`,
        method: "POST",
        headers: this.get_request_headers(),
        data: form_data,
        processData: false,
        contentType: false,
        success: (response_data) => {
          this.handle_campaign_update_success(response_data?.data || {});
        },
        error: (error) => {
          this.handle_form_request_error(error, "Unable to update campaign.");
        },
        complete: () => {
          this.is_saving_campaign = false;
        },
      });
    },
    ...campaign_view_small_methods,
    ...campaign_state_methods,
    ...campaign_delete_methods,
    ...my_donation_campaigns_methods,
    ...my_donation_campaigns_flow_methods,
    get_campaign_cover_image,
  },
};
